import React from 'react';
import { useParams } from 'react-router-dom';

import Header from '@components/common/Header/Header';
import CopyLinkIconBtn from '@components/common/Header/components/BtnHeaderCopyLink/BtnHeaderCopyLink';

import useTranslation from '@hooks/useTranslation';

import type { ILocation } from '@model/location';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';

import EquipmentAssociated from './components/EquipmentAssociated/EquipmentAssociated';
import EquipmentDetailsAIHistory from './components/EquipmentDetailsAIHistory/EquipmentDetailsAIHistory';
import EquipmentDetailsInformation from './components/EquipmentDetailsInformation/EquipmentDetailsInformation';
import SensorsAssociated from './components/SensorsAssociated/SensorsAssociated';
import { getDetailsData } from './helpers/getDetailsData';
import { useHardwareOverviewEquipmentItemQuery } from './services/useHardwareOverviewEquipmentItemQuery';
import style from './style.module.scss';

const EquipmentDetails = () => {
  const { equipmentType, itemId } = useParams();
  const { t } = useTranslation();

  const currentEquipmentType = equipmentType === 'repeater' ? 'router' : equipmentType;

  const { data: assetTreeData } = useAssetTreeQuery();
  const { data: equipmentData, isFetching } = useHardwareOverviewEquipmentItemQuery({
    equipmentType: currentEquipmentType,
    itemId,
  });

  const equipmentModel = equipmentData?.model;
  const actionListHistory = equipmentData?.actionListHistory;
  const locTable =
    equipmentModel?.locationTable || equipmentModel?.equipment?.locationTable || equipmentModel?.location;
  const facilityName = equipmentModel?.gateway
    ? equipmentModel?.gateway.facility?.name
    : equipmentModel?.facility?.name;
  const sensorsLocation = equipmentModel?.sensors_location_id
    ? assetTreeData?.locations?.find(
        (loc: ILocation) =>
          equipmentModel?.sensors_location_id && +loc.id === +equipmentModel.sensors_location_id,
      )
    : undefined;

  const locationName = locTable?.name || sensorsLocation?.name;

  const headerInfo = `${facilityName || locTable?.facility?.name || '-'} / ${locationName || '-'}`;

  const copyDetailsData = getDetailsData(t, equipmentModel, currentEquipmentType);

  return (
    <div className={style.equipmentDetails}>
      <Header
        title={`${t(equipmentType)} ${t('information')}: ${equipmentModel?.serial || ''}`}
        isSecondaryPage={true}
        headerRightComponent={<CopyLinkIconBtn />}
        extraInfo={
          <div className={style.extraInfoWrapper}>
            {t('Location')}: <span className={style.extraInfo}>{headerInfo}</span>
          </div>
        }
      />
      <div className={style.equipmentDetailsBody}>
        {equipmentData && (
          <>
            <EquipmentDetailsInformation
              detailsData={equipmentModel}
              copyDetailsData={copyDetailsData}
              locationName={locationName}
              isEquipmentLoading={isFetching}
            />
            <EquipmentDetailsAIHistory
              historyList={actionListHistory}
              detailsData={equipmentModel}
              currentEquipmentType={currentEquipmentType}
              isEquipmentLoading={isFetching}
            />
            <div className={style.associatedBlock}>
              {equipmentType === 'tachometer' && (
                <EquipmentAssociated detailsData={equipmentModel} isEquipmentLoading={isFetching} />
              )}
              {equipmentType === 'node' && (
                <SensorsAssociated detailsData={equipmentModel} isEquipmentLoading={isFetching} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EquipmentDetails;
